import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { paymentAPI } from '../../services/api';
import { getTimeUntilExpiration } from '../../utils/formatters';
import { PAYMENT_STATUS_OPTIONS, PAGINATION } from '../../utils/constants';
import { useAdminTheme } from '../../contexts/AdminThemeContext';
import PaymentCard from './PaymentCard';
import PaymentTable from './PaymentTable';
import PaymentFilters from './PaymentFilters';
import Loader from '../common/Loader';

const PaymentList = forwardRef(({ initialFilters = {}, hideHeader = false }, ref) => {
  const { isDarkMode } = useAdminTheme();
  const [payments, setPayments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: 'all',
    search: '',
    startDate: '',
    endDate: '',
    page: PAGINATION.DEFAULT_PAGE,
    limit: PAGINATION.DEFAULT_LIMIT,
    ...initialFilters
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    hasNextPage: false,
    hasPrevPage: false
  });
  
  // Cache management
  const [cache, setCache] = useState({});
  const CACHE_DURATION = 15000; // 15 seconds cache for payment list

  const getCacheKey = (filters) => {
    return JSON.stringify(filters);
  };

  const fetchPayments = useCallback(async (forceRefresh = false) => {
    const cacheKey = getCacheKey(filters);
    
    // Helper function to check if cache is valid
    const isCacheValid = (cacheKey) => {
      const now = Date.now();
      return cache[cacheKey]?.timestamp && (now - cache[cacheKey].timestamp) < CACHE_DURATION;
    };
    
    // Use cache if available and not forcing refresh
    if (!forceRefresh && isCacheValid(cacheKey)) {
      const cachedData = cache[cacheKey].data;
      setPayments(cachedData.paymentRequests);
      setPagination(cachedData.pagination);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const response = await paymentAPI.getPaymentRequests(filters);
      
      if (response.success) {
        setPayments(response.data.paymentRequests);
        setPagination(response.data.pagination);
        
        // Update cache
        setCache(prev => ({
          ...prev,
          [cacheKey]: { 
            data: response.data, 
            timestamp: Date.now() 
          }
        }));
      } else {
        toast.error('Failed to fetch payments');
      }
    } catch (error) {
      console.error('Error fetching payments:', error);
      toast.error('Failed to fetch payments');
    } finally {
      setIsLoading(false);
    }
  }, [filters, cache]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters,
      page: 1 // Reset to first page when filters change
    }));
  };

  const handlePageChange = (page) => {
    setFilters(prev => ({
      ...prev,
      page
    }));
  };

  const handleRefresh = () => {
    fetchPayments(true); // Force refresh
  };

  // Expose refresh function to parent component
  useImperativeHandle(ref, () => ({
    refresh: handleRefresh
  }));

  const handlePaymentUpdate = (paymentId, updates) => {
    setPayments(prevPayments => 
      prevPayments.map(payment => 
        payment._id === paymentId 
          ? { ...payment, ...updates }
          : payment
      )
    );
  };

  const getExpiredPayments = () => {
    return payments.filter(payment => {
      if (payment.status !== 'pending') return false;
      const timeUntilExpiration = getTimeUntilExpiration(payment.expiresAt);
      return timeUntilExpiration.expired;
    });
  };

  const expiredPayments = getExpiredPayments();

  return (
    <div className="space-y-6">
      {/* Header */}
      {!hideHeader && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className={`text-2xl font-bold transition-colors duration-300 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>Payment Requests</h1>
            <p className={`mt-1 text-sm transition-colors duration-300 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-600'
            }`}>
              Manage and track all your payment requests
            </p>
          </div>
          <div className="mt-4 sm:mt-0">
            <button
              onClick={handleRefresh}
              className={isDarkMode ? 'btn btn-secondary-dark' : 'btn btn-secondary'}
              disabled={isLoading}
            >
              {isLoading ? <Loader size="sm" text="" isDarkMode={isDarkMode} /> : 'Refresh'}
            </button>
          </div>
        </div>
      )}

      {/* Expired Payments Alert */}
      {expiredPayments.length > 0 && (
        <div className={`border rounded-md p-4 transition-colors duration-300 ${
          isDarkMode 
            ? 'bg-yellow-900/20 border-yellow-700' 
            : 'bg-warning-50 border-warning-200'
        }`}>
          <div className="flex">
            <div className="flex-shrink-0">
              <span className={isDarkMode ? 'text-yellow-400' : 'text-warning-400'}>⚠️</span>
            </div>
            <div className="ml-3">
              <h3 className={`text-sm font-medium transition-colors duration-300 ${
                isDarkMode ? 'text-yellow-200' : 'text-warning-800'
              }`}>
                {expiredPayments.length} payment request{expiredPayments.length > 1 ? 's' : ''} expired
              </h3>
              <div className={`mt-2 text-sm transition-colors duration-300 ${
                isDarkMode ? 'text-yellow-300' : 'text-warning-700'
              }`}>
                <p>
                  Some payment requests have expired and may need to be recreated.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <PaymentFilters
        filters={filters}
        onFilterChange={handleFilterChange}
        statusOptions={PAYMENT_STATUS_OPTIONS}
      />

      {/* Results Summary */}
      <div className={`flex items-center justify-between text-sm transition-colors duration-300 ${
        isDarkMode ? 'text-gray-400' : 'text-gray-600'
      }`}>
        <div>
          Showing {payments.length} of {pagination.totalCount} payment requests
        </div>
        <div>
          Page {pagination.currentPage} of {pagination.totalPages}
        </div>
      </div>

      {/* Loading State */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader size="lg" text="Loading payments..." isDarkMode={isDarkMode} />
        </div>
      ) : payments.length === 0 ? (
        /* Empty State */
        <div className="text-center py-12">
          <div className={`mx-auto h-12 w-12 transition-colors duration-300 ${
            isDarkMode ? 'text-gray-500' : 'text-gray-400'
          }`}>
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <h3 className={`mt-2 text-sm font-medium transition-colors duration-300 ${
            isDarkMode ? 'text-gray-100' : 'text-gray-900'
          }`}>No payment requests</h3>
          <p className={`mt-1 text-sm transition-colors duration-300 ${
            isDarkMode ? 'text-gray-400' : 'text-gray-500'
          }`}>
            Get started by creating your first payment request.
          </p>
          <div className="mt-6">
            <Link to="/admin/create-payment" className="btn btn-primary">
              Create Payment Request
            </Link>
          </div>
        </div>
      ) : (
        /* Payment List */
        <div className="space-y-4">
          {/* Desktop Table View */}
          <div className="hidden md:block">
            <PaymentTable
              payments={payments}
              onRefresh={handleRefresh}
              onPaymentUpdate={handlePaymentUpdate}
            />
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden space-y-4">
            {payments.map((payment) => (
              <PaymentCard
                key={payment._id}
                payment={payment}
                onRefresh={handleRefresh}
              />
            ))}
          </div>
        </div>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className={`flex items-center justify-between border-t px-4 py-3 sm:px-6 transition-colors duration-300 ${
          isDarkMode 
            ? 'border-skyone-border-dark bg-skyone-card-dark' 
            : 'border-gray-200 bg-white'
        }`}>
          <div className="flex flex-1 justify-between sm:hidden">
            <button
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={!pagination.hasPrevPage}
              className={`relative inline-flex items-center rounded-md border px-4 py-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300 ${
                isDarkMode
                  ? 'border-skyone-border-dark bg-skyone-inset-dark text-gray-200 hover:bg-skyone-border-dark'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={!pagination.hasNextPage}
              className={`relative ml-3 inline-flex items-center rounded-md border px-4 py-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300 ${
                isDarkMode
                  ? 'border-skyone-border-dark bg-skyone-inset-dark text-gray-200 hover:bg-skyone-border-dark'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className={`text-sm transition-colors duration-300 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Showing{' '}
                <span className="font-medium">
                  {(pagination.currentPage - 1) * pagination.limit + 1}
                </span>{' '}
                to{' '}
                <span className="font-medium">
                  {Math.min(pagination.currentPage * pagination.limit, pagination.totalCount)}
                </span>{' '}
                of{' '}
                <span className="font-medium">{pagination.totalCount}</span>{' '}
                results
              </p>
            </div>
            <div>
              <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                <button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={!pagination.hasPrevPage}
                  className={`relative inline-flex items-center rounded-l-md px-2 py-2 ring-1 ring-inset focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300 ${
                    isDarkMode
                      ? 'text-gray-400 ring-skyone-border-dark hover:bg-skyone-inset-dark'
                      : 'text-gray-400 ring-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <span className="sr-only">Previous</span>
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                  </svg>
                </button>
                
                {/* Page Numbers */}
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  const pageNum = Math.max(1, pagination.currentPage - 2) + i;
                  if (pageNum > pagination.totalPages) return null;
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold transition-colors duration-300 ${
                        pageNum === pagination.currentPage
                          ? 'z-10 bg-primary-600 text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600'
                          : isDarkMode
                            ? 'text-gray-200 ring-1 ring-inset ring-skyone-border-dark hover:bg-skyone-inset-dark focus:z-20 focus:outline-offset-0'
                            : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                
                <button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={!pagination.hasNextPage}
                  className={`relative inline-flex items-center rounded-r-md px-2 py-2 ring-1 ring-inset focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300 ${
                    isDarkMode
                      ? 'text-gray-400 ring-skyone-border-dark hover:bg-skyone-inset-dark'
                      : 'text-gray-400 ring-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <span className="sr-only">Next</span>
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default PaymentList;
